import { SelectField } from '@/components/ui/select/field'
import { QUERY_KEY_COMBINATIONS, QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useUploadFile from '@/hooks/useUploadFile'
import SendIcons from '@/icons/SendIcons'
import { Attachment } from '@/modules/Upload/types'
import { yupResolver } from '@hookform/resolvers/yup'
import { Paperclip } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { PROCESS_FEEDBACK_STATUS, STORGE_PREFIXS } from '../../constants'
import { createContractProcessHistory } from '../../services'
import { CreateProcessResolveParams } from '../../types'

const schemaWithStatus = yup.object().shape({
    process_feedback: yup.string().required(),
    process_feedback_status: yup.string().required(),
})

const schemaMessageOnly = yup.object().shape({
    process_feedback: yup.string().required(),
    process_feedback_status: yup.string().optional(),
})

interface ProcessFeedbackFormProps {
    contractID: number
    contractProcessID: number
    /** Hide status dropdown — creator can only post message, not change status */
    hideStatus?: boolean
    onSuccess?: () => void
}

const ProcessFeedbackForm: React.FC<ProcessFeedbackFormProps> = ({
    contractID,
    contractProcessID,
    hideStatus = false,
    onSuccess,
}) => {
    const { t } = useTranslation()
    const { uploadAsync, isUploading } = useUploadFile({
        prefix: STORGE_PREFIXS.CONTRACT_PROCESS,
    })
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])

    const { refetch: callbackProcess } = useQueryClientCallback({
        pattern: 'processes',
        mode: 'contains',
    })
    const { refetch: callbackContract } = useQueryClientCallback({
        pattern: 'customers.contracts',
        mode: 'contains',
    })

    const formControl = useForm<CreateProcessResolveParams>({
        defaultValues: {
            process_feedback: '',
            process_feedback_status: 'in-progress',
        },
        resolver: yupResolver(
            (hideStatus ? schemaMessageOnly : schemaWithStatus) as any
        ),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: createContractProcessHistory,
        },
        {
            successMessage: t('resolve-process-success'),
            reactQueryKeys: [
                ...QUERY_KEY_COMBINATIONS.PROCESS_WITH_CONTRACT,
                QUERY_KEYS.PROCESSES_HISTORIES,
            ],
            onSuccess: () => {
                formControl.reset({
                    process_feedback: '',
                    process_feedback_status: 'in-progress',
                })
                setSelectedFiles([])
                callbackProcess()
                callbackContract()
                onSuccess?.()
            },
        }
    )

    const handleSubmit = async (values: CreateProcessResolveParams) => {
        // Require attachments when marking as completed
        if (
            values.process_feedback_status === 'completed' &&
            selectedFiles.length === 0
        ) {
            toast.error(t('attachment-required-for-completed'))
            return
        }

        let attachments: Attachment[] = []
        if (selectedFiles.length > 0) {
            const attachmentUploadeds = await uploadAsync(selectedFiles)

            attachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]
        }

        await mutateAsync({
            contractID,
            contractProcessID,
            data: {
                ...values,
                attachments: attachments,
            },
        })
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setSelectedFiles(Array.from(e.target.files))
        }
    }

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
    }

    return (
        <div className="border border-gray-200 rounded-lg mt-4">
            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
                <div className="px-3 pt-2 space-y-1">
                    {selectedFiles.map((file, index) => (
                        <div
                            key={index}
                            className="flex items-center justify-between text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200"
                        >
                            <span className="text-gray-700 truncate flex-1">
                                {file.name}
                            </span>
                            <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="text-red-500 hover:text-red-700 ml-2 shrink-0"
                                disabled={isPending || isUploading}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Status selector (only for assignees) */}
            {!hideStatus && (
                <div className="px-3 pt-2">
                    <Controller
                        control={formControl.control}
                        name="process_feedback_status"
                        render={({ field }) => (
                            <SelectField
                                value={field.value}
                                onValueChange={field.onChange}
                                options={PROCESS_FEEDBACK_STATUS(t).map(
                                    (s) => ({
                                        label: s.label,
                                        value: s.value,
                                    })
                                )}
                                placeholder={t('process_feedback_status')}
                                size="sm"
                                disabled={isPending || isUploading}
                            />
                        )}
                    />
                </div>
            )}

            {/* Livechat-style input bar */}
            <form
                onSubmit={formControl.handleSubmit(handleSubmit)}
                className="flex items-center gap-2 px-3 py-2.5"
            >
                {/* Attachment button */}
                <label className="cursor-pointer shrink-0 w-8 h-8 flex items-center justify-center rounded-full border border-blue-300 hover:bg-blue-50 transition-colors">
                    <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={isPending || isUploading}
                    />
                    <Paperclip className="w-4 h-4 text-blue-500" />
                </label>

                {/* Message input */}
                <Controller
                    control={formControl.control}
                    name="process_feedback"
                    render={({ field }) => (
                        <input
                            {...field}
                            type="text"
                            placeholder="Type a message..."
                            className="flex-1 text-sm bg-transparent outline-none text-gray-800 placeholder:text-gray-400 py-1"
                            disabled={isPending || isUploading}
                        />
                    )}
                />

                {/* Send button */}
                <button
                    type="submit"
                    disabled={isPending || isUploading}
                    className="shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-primary transition-colors disabled:opacity-50"
                >
                    <SendIcons className="w-5 h-5" />
                </button>
            </form>
        </div>
    )
}

export default ProcessFeedbackForm
